import * as React from 'react'
import clsx from 'clsx'
import * as styles from './Table.css'

// =============================================================================
// Context for sharing table settings across components
// =============================================================================

type TableSize = 'sm' | 'md' | 'lg'

interface TableContextValue {
  size: TableSize
}

const TableContext = React.createContext<TableContextValue>({
  size: 'md',
})

function useTableContext() {
  return React.useContext(TableContext)
}

// =============================================================================
// Table.Root
// =============================================================================

export interface RootProps extends React.ComponentPropsWithoutRef<'table'> {
  /** Table size variant */
  size?: TableSize
  /** Make the header sticky (stays visible when scrolling vertically) */
  stickyHeader?: boolean
  /** Make the footer sticky (stays visible when scrolling vertically) */
  stickyFooter?: boolean
  /** Pin the first column to the left (stays visible when scrolling horizontally) */
  pinnedFirst?: boolean
  /** Pin the last column to the right (stays visible when scrolling horizontally) */
  pinnedLast?: boolean
}

const Root = React.forwardRef<HTMLTableElement, RootProps>(
  (
    {
      size = 'md',
      stickyHeader = false,
      stickyFooter = false,
      pinnedFirst = false,
      pinnedLast = false,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const contextValue = React.useMemo(() => ({ size }), [size])

    return (
      <div className={styles.tableWrapper}>
        <TableContext.Provider value={contextValue}>
          <table
            ref={ref}
            className={clsx(styles.root, className)}
            style={style}
            data-sticky-header={stickyHeader || undefined}
            data-sticky-footer={stickyFooter || undefined}
            data-pinned-first={pinnedFirst || undefined}
            data-pinned-last={pinnedLast || undefined}
            {...props}
          >
            {children}
          </table>
        </TableContext.Provider>
      </div>
    )
  }
)
Root.displayName = 'Table.Root'

// =============================================================================
// Table.Header
// =============================================================================

export interface HeaderProps extends React.ComponentPropsWithoutRef<'thead'> {}

const Header = React.forwardRef<HTMLTableSectionElement, HeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <thead ref={ref} className={clsx(styles.header, className)} {...props}>
        {children}
      </thead>
    )
  }
)
Header.displayName = 'Table.Header'

// =============================================================================
// Table.Body
// =============================================================================

export interface BodyProps extends React.ComponentPropsWithoutRef<'tbody'> {}

const Body = React.forwardRef<HTMLTableSectionElement, BodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <tbody ref={ref} className={clsx(styles.body, className)} {...props}>
        {children}
      </tbody>
    )
  }
)
Body.displayName = 'Table.Body'

// =============================================================================
// Table.Footer
// =============================================================================

export interface FooterProps extends React.ComponentPropsWithoutRef<'tfoot'> {}

const Footer = React.forwardRef<HTMLTableSectionElement, FooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <tfoot ref={ref} className={clsx(styles.footer, className)} {...props}>
        {children}
      </tfoot>
    )
  }
)
Footer.displayName = 'Table.Footer'

// =============================================================================
// Table.Row
// =============================================================================

export interface RowProps extends React.ComponentPropsWithoutRef<'tr'> {
  hoverable?: boolean
}

const Row = React.forwardRef<HTMLTableRowElement, RowProps>(
  ({ hoverable = false, className, children, ...props }, ref) => {
    return (
      <tr
        ref={ref}
        className={clsx(styles.row({ hoverable }), className)}
        {...props}
      >
        {children}
      </tr>
    )
  }
)
Row.displayName = 'Table.Row'

// =============================================================================
// Table.ColumnHeaderCell
// =============================================================================

export interface ColumnHeaderCellProps
  extends React.ComponentPropsWithoutRef<'th'> {
  /** Text alignment */
  align?: 'left' | 'center' | 'right'
  /** Column width */
  width?: string | number
}

const ColumnHeaderCell = React.forwardRef<
  HTMLTableCellElement,
  ColumnHeaderCellProps
>(({ align = 'left', width, className, style, children, ...props }, ref) => {
  const { size } = useTableContext()

  return (
    <th
      ref={ref}
      className={clsx(styles.columnHeaderCell({ size, align }), className)}
      style={{ ...style, width }}
      {...props}
    >
      {children}
    </th>
  )
})
ColumnHeaderCell.displayName = 'Table.ColumnHeaderCell'

// =============================================================================
// Table.RowHeaderCell
// =============================================================================

export interface RowHeaderCellProps
  extends React.ComponentPropsWithoutRef<'th'> {
  /** Text alignment */
  align?: 'left' | 'center' | 'right'
  /** Column width */
  width?: string | number
}

const RowHeaderCell = React.forwardRef<
  HTMLTableCellElement,
  RowHeaderCellProps
>(({ align = 'left', width, className, style, children, ...props }, ref) => {
  const { size } = useTableContext()

  return (
    <th
      ref={ref}
      scope="row"
      className={clsx(styles.rowHeaderCell({ size, align }), className)}
      style={{ ...style, width }}
      {...props}
    >
      {children}
    </th>
  )
})
RowHeaderCell.displayName = 'Table.RowHeaderCell'

// =============================================================================
// Table.Cell
// =============================================================================

export interface CellProps extends React.ComponentPropsWithoutRef<'td'> {
  /** Text alignment */
  align?: 'left' | 'center' | 'right'
  /** Column width */
  width?: string | number
}

const Cell = React.forwardRef<HTMLTableCellElement, CellProps>(
  ({ align = 'left', width, className, style, children, ...props }, ref) => {
    const { size } = useTableContext()

    return (
      <td
        ref={ref}
        className={clsx(styles.cell({ size, align }), className)}
        style={{ ...style, width }}
        {...props}
      >
        {children}
      </td>
    )
  }
)
Cell.displayName = 'Table.Cell'

// =============================================================================
// Table.ColumnFooterCell
// =============================================================================

export interface ColumnFooterCellProps extends React.ComponentPropsWithoutRef<'td'> {
  /** Text alignment */
  align?: 'left' | 'center' | 'right'
  /** Column width */
  width?: string | number
}

const ColumnFooterCell = React.forwardRef<HTMLTableCellElement, ColumnFooterCellProps>(
  ({ align = 'left', width, className, style, children, ...props }, ref) => {
    const { size } = useTableContext()

    return (
      <td
        ref={ref}
        className={clsx(styles.columnFooterCell({ size, align }), className)}
        style={{ ...style, width }}
        {...props}
      >
        {children}
      </td>
    )
  }
)
ColumnFooterCell.displayName = 'Table.ColumnFooterCell'

// =============================================================================
// Compound Component Export
// =============================================================================

export const Table = {
  Root,
  Header,
  Body,
  Footer,
  Row,
  ColumnHeaderCell,
  RowHeaderCell,
  Cell,
  ColumnFooterCell,
}
